import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { TextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import type { CrudFormProps } from '../../components/CrudPage';
import { CrudPage } from '../../components/CrudPage';
import type { ResourceDto } from '../../api/models/ResourceDto';
import type { ResourceTypeDto } from '../../api/models/ResourceTypeDto';
import type { ShiftAssignmentDto } from '../../api/models/ShiftAssignmentDto';
import type { ShiftTemplateDto } from '../../api/models/ShiftTemplateDto';
import { ResourceControllerService, ResourceTypeControllerService, ShiftTemplateControllerService, ShiftAssignmentControllerService } from '../../api';
import { useErrorHandler } from '../../utils/errorHandler';
import { useToast } from '../../hooks/useToast';
import { useDropdownFormInitializer } from '../../hooks/useDropdownFormInitializer';
import ShiftAssignmentFormDialog from './ShiftAssignmentFormDialog';
import { generateTempUUID, isTempUUID } from '../../utils/uuid';

// Type for resource with string id for CrudPage compatibility
type ResourceWithStringId = Omit<ResourceDto, 'id'> & { id: string };

// Validation schema for resource
const resourceSchema = z.object({
  name: z.string().min(1, 'validation.required'),
  description: z.string().optional(),
  resourceTypeUuid: z.string().min(1, 'validation.required'),
  shiftAssignments: z.array(z.object({
    uuid: z.string().optional(),
    resourceId: z.string().optional(),
    assignmentStart: z.string().optional(),
    assignmentEnd: z.string().optional(),
    shiftTemplateId: z.string().optional(),
    capacityValue: z.number().optional(),
    priority: z.number().optional(),
    overwritesLowerPriority: z.boolean().optional()
  })).optional()
});

type ResourceFormData = z.infer<typeof resourceSchema>;

const ResourceFormDialog: React.FC<CrudFormProps<ResourceWithStringId>> = ({ value, onClose, onSave, isClone }) => {
  const { t } = useTranslation(['common']);
  const [visible, setVisible] = useState(false);
  const [resourceTypes, setResourceTypes] = useState<ResourceTypeDto[]>([]);
  const [shiftTemplates, setShiftTemplates] = useState<ShiftTemplateDto[]>([]);
  const [shiftAssignments, setShiftAssignments] = useState<ShiftAssignmentDto[]>([]);
  const [shiftAssignmentsRefreshKey, setShiftAssignmentsRefreshKey] = useState(0);
  const { handleError } = useErrorHandler();
  const { showToast } = useToast();
  
  // Store the resource UUID for use in FormComponent (where value is the assignment, not the resource)
  const resourceUuid = value?.uuid || '';

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<ResourceFormData>({
    resolver: zodResolver(resourceSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      description: '',
      resourceTypeUuid: '',
      shiftAssignments: []
    }
  });

  const currentValue = watch(); // Watch all form values (similar to RoutingFormDialog)

  // Initialize shiftAssignments state when form data changes (similar to RoutingFormDialog)
  useEffect(() => {
    setShiftAssignments(currentValue.shiftAssignments || []);
    // Force refresh of the CrudPage when shiftAssignments change
    setShiftAssignmentsRefreshKey(prev => prev + 1);
  }, [currentValue.shiftAssignments]);

  useEffect(() => {
    if (shiftTemplates.length > 0) {
      setShiftAssignmentsRefreshKey(prev => prev + 1);
    }
  }, [shiftTemplates.length]);

  useEffect(() => {
    if (value) {
      const assignments = value.shiftAssignments || [];
      // Set both state and form value
      setShiftAssignments(assignments);
      setValue('shiftAssignments', assignments);
    } else {
      setShiftAssignments([]);
      setValue('shiftAssignments', []);
    }
    setVisible(true);
  }, [value, setValue]);

  // Load data function for the hook
  const loadData = async () => {
    try {
      const [resourceTypesData, shiftTemplatesData] = await Promise.all([
        ResourceTypeControllerService.findAll(),
        ShiftTemplateControllerService.findAll().catch(err => {
          // If shift templates can't be loaded (e.g., permission issue), continue without them
          console.warn('Could not load shift templates:', err);
          return [];
        })
      ]);
      return { 
        resourceTypes: resourceTypesData, 
        shiftTemplates: shiftTemplatesData 
      };
    } catch (error) {
      // If resource types can't be loaded, return empty arrays
      console.error('Error loading form data:', error);
      return {
        resourceTypes: [],
        shiftTemplates: []
      };
    }
  };

  // Set state functions for the hook
  const setStateFunctions = {
    resourceTypes: setResourceTypes,
    shiftTemplates: setShiftTemplates
  };

  // Store the current value to use after dropdowns are loaded
  const valueRef = React.useRef(value);
  React.useEffect(() => {
    valueRef.current = value;
  }, [value]);

  // Custom reset function that preserves values after dropdowns load
  const resetWithValue = React.useCallback((values: any) => {
    // Always use the current value if available, otherwise use provided values
    const currentValue = valueRef.current;
    if (currentValue) {
      const finalValues = {
        name: currentValue.name || '',
        description: currentValue.description || '',
        resourceTypeUuid: currentValue.resourceTypeUuid?.toString() || '',
        shiftAssignments: currentValue.shiftAssignments || []
      };
      // Reset immediately - the dropdowns should be loaded by now
      reset(finalValues);
    } else {
      reset(values);
    }
  }, [reset]);

  // Get initial values based on current value
  const getInitialValues = () => {
    const currentValue = valueRef.current;
    if (currentValue) {
      return {
        name: currentValue.name || '',
        description: currentValue.description || '',
        resourceTypeUuid: currentValue.resourceTypeUuid?.toString() || '',
        shiftAssignments: currentValue.shiftAssignments || []
      };
    }
    return {
      name: '',
      description: '',
      resourceTypeUuid: '',
      shiftAssignments: []
    };
  };

  // Use the custom hook for proper timing - pass function to get fresh values
  useDropdownFormInitializer(
    visible,
    loadData,
    setStateFunctions,
    resetWithValue,
    getInitialValues
  );

  // Also reset form when value changes and resourceTypes are already loaded
  // This ensures values are set even if the hook has already run
  useEffect(() => {
    if (value && resourceTypes.length > 0 && visible) {
      // Only reset if we have the dropdown data loaded and dialog is visible
      reset({
        name: value.name || '',
        description: value.description || '',
        resourceTypeUuid: value.resourceTypeUuid?.toString() || '',
        shiftAssignments: value.shiftAssignments || []
      });
    }
  }, [value, resourceTypes.length, visible, reset]);

  const onSubmit = async (data: ResourceFormData) => {
    try {
      // Use shiftAssignments from currentValue (form state) as it contains the most up-to-date data
      // Similar to RoutingFormDialog which prefers currentValue.operations
      const finalShiftAssignments = (shiftAssignments && shiftAssignments.length > 0)
        ? shiftAssignments
        : (currentValue.shiftAssignments && currentValue.shiftAssignments.length > 0)
          ? currentValue.shiftAssignments
          : (data.shiftAssignments && data.shiftAssignments.length > 0
            ? data.shiftAssignments
            : []);
      
      const { shiftAssignments: _ignoredShiftAssignments, ...restValue } = value || {};
      const resource: ResourceWithStringId = {
        ...restValue,
        id: value?.uuid || '',
        name: data.name,
        description: data.description,
        resourceTypeUuid: data.resourceTypeUuid
      };
      
      // Save resource first - onSave may or may not return the saved resource
      await onSave(resource);
      
      // Get the resource UUID - use existing if available, otherwise fetch from backend
      let resourceUuid: string | undefined = value?.uuid;
      if (!resourceUuid && resource.id) {
        resourceUuid = resource.id;
      }
      if (!resourceUuid) {
        // If we just created a new resource, we need to fetch it to get the UUID
        const allResources = await ResourceControllerService.findAll();
        const savedResource = allResources.find(r => r.name === data.name);
        resourceUuid = savedResource?.uuid;
      }
      
      // Save shiftAssignments separately via ShiftAssignmentController
      // Use the same pattern as RoutingFormDialog: delete all and recreate all
      if (resourceUuid) {
        const assignmentsToSave = finalShiftAssignments.map(assignment => {
          const cleanedUuid = assignment.uuid && isTempUUID(assignment.uuid) ? undefined : assignment.uuid;
          return {
            ...assignment,
            resourceId: resourceUuid,
            uuid: cleanedUuid
          };
        });

        const savedAssignments = await ShiftAssignmentControllerService.replaceAssignmentsForResource(
          resourceUuid,
          assignmentsToSave
        );
        
        setValue('shiftAssignments', savedAssignments);
        setShiftAssignments(savedAssignments);
        setShiftAssignmentsRefreshKey(prev => prev + 1);

        // Refresh from backend to sync with server-side mapping (e.g. shiftTemplateName)
        const refreshedResource = await ResourceControllerService.findByUuid(resourceUuid);
        if (refreshedResource?.shiftAssignments) {
          setValue('shiftAssignments', refreshedResource.shiftAssignments);
          setShiftAssignments(refreshedResource.shiftAssignments);
          setShiftAssignmentsRefreshKey(prev => prev + 1);
        }

        // Trigger resources list refresh after assignments are fully persisted
        window.dispatchEvent(new CustomEvent('resources:refresh'));
      }
      
      showToast('success', t('common.success'), t('common.saved', 'Gespeichert'));
      
      // Only close dialog if save was successful
      setVisible(false);
    } catch (error) {
      // Handle error but keep dialog open
      handleError(error, 'saving resource');
      // Don't close the dialog on error
    }
  };

  const handleClose = () => {
    setVisible(false);
    onClose();
  };

  // Handle priority changes and persist to backend
  const handlePriorityChange = async (updatedAssignments: ShiftAssignmentDto[]) => {
    try {
      // Update priorities in backend for each assignment
      for (let i = 0; i < updatedAssignments.length; i++) {
        const assignment = updatedAssignments[i];
        if (assignment.uuid && !isTempUUID(assignment.uuid)) {
          await ShiftAssignmentControllerService.update(assignment.uuid, {
            ...assignment,
            priority: i + 1
          });
        }
      }
      // Update both form state and local state (similar to RoutingFormDialog)
      setValue('shiftAssignments', updatedAssignments);
      setShiftAssignments(updatedAssignments);
      showToast('success', t('actions.reorder'), t('messages.reorderSuccess'));
    } catch (error) {
      handleError(error, 'updating assignment priorities');
      // Reload assignments to restore original order
      if (value?.uuid) {
        const resources = await ResourceControllerService.findAll();
        const resource = resources.find(r => r.uuid === value.uuid);
        if (resource) {
          const restoredAssignments = resource.shiftAssignments || [];
          setValue('shiftAssignments', restoredAssignments);
          setShiftAssignments(restoredAssignments);
        }
      }
    }
  };

  // Renderer for shift template name
  const shiftTemplateNameRenderer = (data: any) => {
    // First try to use the name from the backend
    if (data?.shiftTemplateName) {
      return <span>{data.shiftTemplateName}</span>;
    }
    
    // Fallback: resolve name from shiftTemplates list
    if (data?.shiftTemplateId && shiftTemplates.length > 0) {
      const template = shiftTemplates.find(t => t.uuid === data.shiftTemplateId);
      if (template && template.name) {
        return <span>{template.name}</span>;
      }
    }
    
    // If still not found, show the ID or a placeholder
    return <span>{data?.shiftTemplateId || '-'}</span>;
  };

  // Type for shift assignment with string id for CrudPage compatibility
  type ShiftAssignmentWithStringId = Omit<ShiftAssignmentDto, 'id'> & { id: string };

  // Handler for CrudPage delete
  const handleAssignmentDelete = async (id: string) => {
    setShiftAssignments(prev => {
      const sourceAssignments = (prev && prev.length > 0)
        ? prev
        : (currentValue.shiftAssignments || []);
      const updatedAssignments = sourceAssignments.filter(a => a.uuid !== id);
      setValue('shiftAssignments', updatedAssignments, { shouldDirty: true });
      return updatedAssignments;
    });
    setShiftAssignmentsRefreshKey(prev => prev + 1);
  };

  return (
    <>
      <DialogComponent
        width="1000px"
        isModal={true}
        visible={visible}
        close={handleClose}
        header={isClone ? t('dialogs.resource.clone') : (value ? t('dialogs.resource.edit') : t('dialogs.resource.create'))}
        footerTemplate={() => (
          <div className="flex justify-end space-x-2">
            <ButtonComponent
              type="button"
              onClick={handleClose}
              cssClass="e-outline"
            >
              {t('actions.cancel')}
            </ButtonComponent>
            <ButtonComponent
              type="button"
              onClick={handleSubmit(onSubmit)}
              disabled={isSubmitting}
              cssClass="e-primary"
            >
              {isSubmitting ? t('actions.saving') : t('actions.save')}
            </ButtonComponent>
          </div>
        )}
      >
        <div className="p-4 space-y-4">
          {/* Resource Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('form.name')} *
            </label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextBoxComponent
                  {...field}
                  placeholder={t('form.name')}
                  cssClass={errors.name ? 'e-error' : ''}
                />
              )}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{t(errors.name.message || 'validation.required')}</p>
            )}
          </div>

          {/* Resource Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('grid.resourceType')} *
            </label>
            <Controller
              name="resourceTypeUuid"
              control={control}
              render={({ field }) => (
                <DropDownListComponent
                  dataSource={resourceTypes}
                  fields={{ text: 'name', value: 'uuid' }}
                  placeholder={t('grid.resourceType')}
                  cssClass={errors.resourceTypeUuid ? 'e-error' : ''}
                  allowFiltering={true}
                  filterBarPlaceholder={t('actions.search')}
                  value={field.value}
                  change={(e: { value?: string }) => {
                    field.onChange(e.value ?? '');
                  }}
                />
              )}
            />
            {errors.resourceTypeUuid && (
              <p className="text-red-500 text-sm mt-1">{t(errors.resourceTypeUuid.message || 'validation.required')}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('form.description')}
            </label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextBoxComponent
                  {...field}
                  placeholder={t('form.description')}
                  multiline={true}
                />
              )}
            />
          </div>

          {/* Shift Assignments - Using CrudPage for Sub-Entity Management */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('grid.shiftAssignments')} ({shiftAssignments.length})
            </label>
            <div className="h-64">
              <CrudPage<ShiftAssignmentWithStringId>
                key={`shift-assignments-${shiftAssignmentsRefreshKey}`}
                titleKey="grid.shiftAssignments"
                entityName="shiftassignment"
                showTitle={false}
                showSummary={false}
                noPadding={true}
                gridHeight="100%"
                localMode={true}
                refreshKey={shiftAssignmentsRefreshKey}
                gridMeta={[
                  // Priority column removed - it's automatically generated
                  {
                    field: 'assignmentStart',
                    headerKey: 'shiftAssignment.grid.field.assignmentStart',
                    width: 150,
                    render: (data: any) => {
                      const date = new Date(data.assignmentStart);
                      return date.toLocaleDateString('de-DE', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      });
                    }
                  },
                  {
                    field: 'assignmentEnd',
                    headerKey: 'shiftAssignment.grid.field.assignmentEnd',
                    width: 150,
                    render: (data: any) => {
                      const date = new Date(data.assignmentEnd);
                      return date.toLocaleDateString('de-DE', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      });
                    }
                  },
                  {
                    field: 'shiftTemplateName',
                    headerKey: 'shiftAssignment.grid.field.shiftTemplateId',
                    width: 150,
                    render: shiftTemplateNameRenderer
                  },
                  {
                    field: 'capacityValue',
                    headerKey: 'shiftAssignment.grid.field.capacityValue',
                    width: 100
                  },
                  {
                    field: 'overwritesLowerPriority',
                    headerKey: 'shiftAssignment.grid.field.overwritesLowerPriority',
                    width: 120,
                    render: (data: any) => (
                      <span>{data?.overwritesLowerPriority ? t('yes') : t('no')}</span>
                    )
                  }
                ]}
                fetchData={async () => {
                  // Use shiftAssignments state as source of truth to reflect local edits/deletes
                  const sourceAssignments = shiftAssignments || currentValue.shiftAssignments || [];
                  // Return current shiftAssignments with string id
                  return sourceAssignments.map(a => {
                    const resolvedTemplateName = a.shiftTemplateName
                      || (a.shiftTemplateId
                        ? shiftTemplates.find(t => t.uuid === a.shiftTemplateId)?.name
                        : undefined);
                    return {
                      ...a,
                      shiftTemplateName: resolvedTemplateName,
                      id: a.uuid || ''
                    };
                  }) as ShiftAssignmentWithStringId[];
                }}
                saveData={async (assignment: ShiftAssignmentWithStringId) => {
                  // Remove the id field and use uuid for the actual data (similar to RoutingFormDialog)
                  const { id, ...assignmentData } = assignment;
                  // Ensure uuid is set - use uuid if available, otherwise use id (which should be the uuid)
                  if (!assignmentData.uuid && id) {
                    assignmentData.uuid = id;
                  }
                  // Use currentValue.shiftAssignments as source of truth (like RoutingFormDialog uses currentValue.operations)
                  const sourceAssignments = currentValue.shiftAssignments || [];
                  
                  // Determine if this is a new assignment (uuid not found in current list)
                  const existingAssignment = sourceAssignments.find(a => a.uuid === assignmentData.uuid);
                  const isNewAssignment = !existingAssignment;
                  
                  const updatedAssignments = isNewAssignment
                    ? [...sourceAssignments, { 
                        ...assignmentData, 
                        uuid: assignmentData.uuid || generateTempUUID(),
                        priority: sourceAssignments.length + 1
                      }]
                    : sourceAssignments.map(a => a.uuid === assignmentData.uuid ? {
                        ...assignmentData,
                        uuid: assignmentData.uuid,
                        priority: existingAssignment?.priority || a.priority || sourceAssignments.length + 1
                      } : a);
                  
                  // Update both form state and local state (similar to RoutingFormDialog)
                  setValue('shiftAssignments', updatedAssignments);
                  setShiftAssignments(updatedAssignments);
                  setShiftAssignmentsRefreshKey(prev => prev + 1);
                  return assignment;
                }}
                deleteData={handleAssignmentDelete}
                suppressDeleteToast={true}
                FormComponent={({ value, onClose, onSave }: any) => {
                  // Map id back to uuid for ShiftAssignmentFormDialog (like OperationDetailDialog)
                  // Preserve existing uuid if id is empty (important for edit mode)
                  const assignmentWithUuid = value ? { 
                    ...value, 
                    uuid: value.id || value.uuid || '' 
                  } : value;
                  
                  // Find the selected resource type and get its singleResource property
                  const selectedResourceType = currentValue.resourceTypeUuid 
                    ? resourceTypes.find(rt => rt.uuid === currentValue.resourceTypeUuid)
                    : null;
                  const isSingleResource = selectedResourceType?.singleResource ?? false;
                  
                  return (
                    <ShiftAssignmentFormDialog
                      value={assignmentWithUuid}
                      onClose={onClose}
                      onSave={(assignment: ShiftAssignmentDto) => {
                        // Map uuid back to id for CrudPage (like OperationDetailDialog)
                        const assignmentWithId = { ...assignment, id: assignment.uuid || '' } as ShiftAssignmentWithStringId;
                        // Only call onSave - it will call saveData which updates shiftAssignments
                        onSave(assignmentWithId);
                      }}
                      shiftTemplates={shiftTemplates}
                      resourceId={resourceUuid}
                      isSingleResource={isSingleResource}
                    />
                  );
                }}
                hasPriority={true}
                priorityField="priority"
                showCloneButton={false}
                onPriorityChange={async (items) => {
                  const updatedAssignments = items.map(item => {
                    const { id, ...rest } = item;
                    return rest as ShiftAssignmentDto;
                  });
                  await handlePriorityChange(updatedAssignments);
                }}
                showSearchButton={false}
                allowFiltering={false}
              />
            </div>
          </div>
        </div>
      </DialogComponent>
    </>
  );
};

export default ResourceFormDialog;
